import type { ColumnDefinition, Column } from '../../api/spreadsheet';
import {
  isPrimaryColumnName,
  isPrioritizationColumnName
} from '../../api/spreadsheet';
import { ENRICHING_CELL_STYLE } from '../../utils/spreadsheetValidation';
import { createAIColumnHeaderIconExtension } from './ColumnHeaders/AIColumnHeaderIconExtension';
import { createPrioritizationColumnHeaderIconExtension } from './ColumnHeaders/PrioritizationColumnHeaderIconExtension';
import { createPrimaryColumnHeaderIconExtension } from './ColumnHeaders/PrimaryColumnHeaderIconExtension';
import {
  createEnrichingCellsHighlightRender,
  createFaviconColumnCustomRender,
  createValidationErrorHighlightRender
} from './spreadsheetCellRenders';
import type {
  SheetSetupAndRenders,
  SheetSetupContext
} from './spreadsheetTypes';

/**
 * Create column header extensions (prioritization icon, primary key icon, AI icon), runSetup,
 * and cell custom renders. Call once per Univer sheet creation.
 */
export function createSheetSetupAndRenders(
  ctx: SheetSetupContext
): SheetSetupAndRenders {
  const {
    univerAPI,
    headerStateRef,
    themePrimary,
    columnHeaderColumnsCfg,
    columnNamesByIndex,
    columnData,
    columnHeaderExtensionDisposableRef,
    setSheetVisibleRef,
    setCellsLoading,
    getValidationErrorsMap,
    getExecutingCellKeys,
    invalidCellStyle
  } = ctx;

  const getPrimaryColumnIndices = () => {
    const state = headerStateRef.current;
    const names = state?.columnNamesByIndex ?? [];
    const set = new Set<number>();
    names.forEach((name, i) => {
      if (isPrimaryColumnName(name)) set.add(i);
    });
    return set;
  };

  const getAiColumnIndices = () => {
    const state = headerStateRef.current;
    if (!state?.columns?.length) return new Set<number>();
    const set = new Set<number>();
    state.columns.forEach((col, i) => {
      const c = col as Column & {
        column_data?: { definition?: ColumnDefinition };
      };
      if (c?.definition ?? c?.column_data?.definition) set.add(i);
    });
    return set;
  };

  const getPrioritizationColumnIndices = () => {
    const state = headerStateRef.current;
    const names = state?.columnNamesByIndex ?? [];
    const set = new Set<number>();
    names.forEach((name, i) => {
      if (isPrioritizationColumnName(name)) set.add(i);
    });
    return set;
  };

  const prioritizationColumnHeaderIconExtension =
    createPrioritizationColumnHeaderIconExtension(
      getPrioritizationColumnIndices,
      () => {
        univerAPI.getActiveWorkbook()?.getActiveSheet()?.refreshCanvas?.();
      },
      themePrimary,
      (colIndex: number) => {
        const state = headerStateRef.current;
        const name = state?.columnNamesByIndex[colIndex] ?? `Column ${colIndex}`;
        return name.startsWith('$') ? name.slice(1) : name;
      }
    );

  const primaryColumnHeaderIconExtension = createPrimaryColumnHeaderIconExtension(
    getPrimaryColumnIndices,
    () => {
      univerAPI.getActiveWorkbook()?.getActiveSheet()?.refreshCanvas?.();
    },
    themePrimary,
    (colIndex: number) => {
      const state = headerStateRef.current;
      const name = state?.columnNamesByIndex[colIndex] ?? `Column ${colIndex}`;
      return name.startsWith('$') ? name.slice(1) : name;
    }
  );

  const aiColumnHeaderExtension = createAIColumnHeaderIconExtension(
    getAiColumnIndices,
    () => {
      univerAPI.getActiveWorkbook()?.getActiveSheet()?.refreshCanvas?.();
    },
    themePrimary,
    (colIndex: number) => {
      const state = headerStateRef.current;
      const name = state?.columnNamesByIndex[colIndex] ?? `Column ${colIndex}`;
      return name.startsWith('$') ? name.slice(1) : name;
    }
  );

  let setupDone = false;
  const runSetup = () => {
    if (setupDone) return;
    const fWorkbook = univerAPI.getActiveWorkbook();
    const fWorksheet = fWorkbook?.getActiveSheet();
    if (!fWorksheet) return;
    if (typeof fWorksheet.customizeColumnHeader === 'function') {
      fWorksheet.customizeColumnHeader({ columnsCfg: columnHeaderColumnsCfg });
    }
    const unitId = fWorkbook?.getId?.();
    if (
      typeof unitId === 'string' &&
      typeof univerAPI.registerSheetColumnHeaderExtension === 'function'
    ) {
      columnHeaderExtensionDisposableRef.current?.dispose();
      const dPrioritizationIcon = univerAPI.registerSheetColumnHeaderExtension(
        unitId,
        prioritizationColumnHeaderIconExtension
      );
      const dPrimaryIcon = univerAPI.registerSheetColumnHeaderExtension(
        unitId,
        primaryColumnHeaderIconExtension
      );
      const dAi = univerAPI.registerSheetColumnHeaderExtension(
        unitId,
        aiColumnHeaderExtension
      );
      columnHeaderExtensionDisposableRef.current = {
        dispose: () => {
          dAi?.dispose?.();
          dPrimaryIcon?.dispose?.();
          dPrioritizationIcon?.dispose?.();
        }
      };
      if (typeof fWorksheet.refreshCanvas === 'function')
        fWorksheet.refreshCanvas();
    }
    if (typeof fWorksheet.setColumnWidth === 'function') {
      for (let c = 0; c < columnNamesByIndex.length; c++) {
        const w = columnData[c]?.w;
        if (typeof w === 'number') fWorksheet.setColumnWidth(c, w);
      }
    }
    setupDone = true;
    requestAnimationFrame(() => {
      setSheetVisibleRef.current?.(true);
      setCellsLoading(false);
    });
  };

  const faviconColumnCustomRender = createFaviconColumnCustomRender({
    getColumnNamesByIndex: () =>
      headerStateRef.current?.columnNamesByIndex ?? [],
    getValidationErrorsMap
  });
  const validationErrorHighlightRender = createValidationErrorHighlightRender({
    getValidationErrorsMap,
    invalidCellStyle
  });
  const enrichingCellsHighlightRender = createEnrichingCellsHighlightRender({
    getExecutingCellKeys: ctx.getExecutingCellKeys,
    enrichingCellStyle: ENRICHING_CELL_STYLE
  });

  return {
    prioritizationColumnHeaderIconExtension,
    primaryColumnHeaderIconExtension,
    aiColumnHeaderExtension,
    runSetup,
    faviconColumnCustomRender,
    validationErrorHighlightRender,
    enrichingCellsHighlightRender
  };
}
